// True NFL brand colors, keyed by ESPN's `displayName`.
//
// This replaces the hand-maintained Tailwind class map in `teamBackground.js`,
// which collapsed a dozen teams onto the same `blue-600` — Giants, Colts,
// Lions, Panthers and Rams were literally indistinguishable — and washed
// everything out to 25% opacity besides.
//
// `primary` and `secondary` are the real brand hexes. They are not used
// directly for anything that has to be *seen* against the dark UI, because
// several of them (Raiders black, Bears navy, Jaguars black) have almost no
// contrast against slate-900. `accent()` handles that.

export const TEAM_COLORS = {
  "Arizona Cardinals": { primary: "#97233F", secondary: "#FFB612" },
  "Atlanta Falcons": { primary: "#A71930", secondary: "#A5ACAF" },
  "Baltimore Ravens": { primary: "#241773", secondary: "#9E7C0C" },
  "Buffalo Bills": { primary: "#00338D", secondary: "#C60C30" },
  "Carolina Panthers": { primary: "#0085CA", secondary: "#BFC0BF" },
  "Chicago Bears": { primary: "#0B162A", secondary: "#C83803" },
  "Cincinnati Bengals": { primary: "#FB4F14", secondary: "#000000" },
  "Cleveland Browns": { primary: "#FF3C00", secondary: "#311D00" },
  "Dallas Cowboys": { primary: "#003594", secondary: "#869397" },
  "Denver Broncos": { primary: "#FB4F14", secondary: "#002244" },
  "Detroit Lions": { primary: "#0076B6", secondary: "#B0B7BC" },
  "Green Bay Packers": { primary: "#203731", secondary: "#FFB612" },
  "Houston Texans": { primary: "#03202F", secondary: "#A71930" },
  "Indianapolis Colts": { primary: "#002C5F", secondary: "#A2AAAD" },
  "Jacksonville Jaguars": { primary: "#006778", secondary: "#D7A22A" },
  "Kansas City Chiefs": { primary: "#E31837", secondary: "#FFB81C" },
  "Las Vegas Raiders": { primary: "#000000", secondary: "#A5ACAF" },
  "Los Angeles Chargers": { primary: "#0080C6", secondary: "#FFC20E" },
  "Los Angeles Rams": { primary: "#003594", secondary: "#FFA300" },
  "Miami Dolphins": { primary: "#008E97", secondary: "#FC4C02" },
  "Minnesota Vikings": { primary: "#4F2683", secondary: "#FFC62F" },
  "New England Patriots": { primary: "#002244", secondary: "#C60C30" },
  "New Orleans Saints": { primary: "#D3BC8D", secondary: "#101820" },
  "New York Giants": { primary: "#0B2265", secondary: "#A71930" },
  "New York Jets": { primary: "#125740", secondary: "#FFFFFF" },
  "Philadelphia Eagles": { primary: "#004C54", secondary: "#A5ACAF" },
  "Pittsburgh Steelers": { primary: "#FFB612", secondary: "#101820" },
  "San Francisco 49ers": { primary: "#AA0000", secondary: "#B3995D" },
  "Seattle Seahawks": { primary: "#69BE28", secondary: "#002244" },
  "Tampa Bay Buccaneers": { primary: "#D50A0A", secondary: "#FF7900" },
  "Tennessee Titans": { primary: "#4B92DB", secondary: "#0C2340" },
  "Washington Commanders": { primary: "#5A1414", secondary: "#FFB612" },
};

// Abbreviation index, so a card can colour itself from `abbr` when the full
// display name is not to hand. `WSH`, not `WAS` — see CLAUDE.md.
const ABBR_TO_NAME = {
  ARI: "Arizona Cardinals", ATL: "Atlanta Falcons", BAL: "Baltimore Ravens",
  BUF: "Buffalo Bills", CAR: "Carolina Panthers", CHI: "Chicago Bears",
  CIN: "Cincinnati Bengals", CLE: "Cleveland Browns", DAL: "Dallas Cowboys",
  DEN: "Denver Broncos", DET: "Detroit Lions", GB: "Green Bay Packers",
  HOU: "Houston Texans", IND: "Indianapolis Colts", JAX: "Jacksonville Jaguars",
  KC: "Kansas City Chiefs", LV: "Las Vegas Raiders", LAC: "Los Angeles Chargers",
  LAR: "Los Angeles Rams", MIA: "Miami Dolphins", MIN: "Minnesota Vikings",
  NE: "New England Patriots", NO: "New Orleans Saints", NYG: "New York Giants",
  NYJ: "New York Jets", PHI: "Philadelphia Eagles", PIT: "Pittsburgh Steelers",
  SF: "San Francisco 49ers", SEA: "Seattle Seahawks", TB: "Tampa Bay Buccaneers",
  TEN: "Tennessee Titans", WSH: "Washington Commanders",
};

// Mascot only, for the narrow team row on mobile -- "Lions", not "Detroit
// Lions". The abbreviation already carries the city, so the full name is
// redundant width a phone-sized card can't spare.
const TEAM_NICKNAMES = {
  "Arizona Cardinals": "Cardinals", "Atlanta Falcons": "Falcons",
  "Baltimore Ravens": "Ravens", "Buffalo Bills": "Bills",
  "Carolina Panthers": "Panthers", "Chicago Bears": "Bears",
  "Cincinnati Bengals": "Bengals", "Cleveland Browns": "Browns",
  "Dallas Cowboys": "Cowboys", "Denver Broncos": "Broncos",
  "Detroit Lions": "Lions", "Green Bay Packers": "Packers",
  "Houston Texans": "Texans", "Indianapolis Colts": "Colts",
  "Jacksonville Jaguars": "Jaguars", "Kansas City Chiefs": "Chiefs",
  "Las Vegas Raiders": "Raiders", "Los Angeles Chargers": "Chargers",
  "Los Angeles Rams": "Rams", "Miami Dolphins": "Dolphins",
  "Minnesota Vikings": "Vikings", "New England Patriots": "Patriots",
  "New Orleans Saints": "Saints", "New York Giants": "Giants",
  "New York Jets": "Jets", "Philadelphia Eagles": "Eagles",
  "Pittsburgh Steelers": "Steelers", "San Francisco 49ers": "49ers",
  "Seattle Seahawks": "Seahawks", "Tampa Bay Buccaneers": "Buccaneers",
  "Tennessee Titans": "Titans", "Washington Commanders": "Commanders",
};

/** Mascot-only name ("Lions"), falling back to the full name if unrecognised. */
export function teamNickname(team) {
  if (!team) return team;
  return TEAM_NICKNAMES[team] || (ABBR_TO_NAME[team] && TEAM_NICKNAMES[ABBR_TO_NAME[team]]) || team;
}

const FALLBACK = { primary: "#64748B", secondary: "#94A3B8" };

function colorsFor(team) {
  if (!team) return FALLBACK;
  return TEAM_COLORS[team] || TEAM_COLORS[ABBR_TO_NAME[team]] || FALLBACK;
}

function hexToRgb(hex) {
  const clean = hex.replace("#", "");
  return {
    r: parseInt(clean.slice(0, 2), 16),
    g: parseInt(clean.slice(2, 4), 16),
    b: parseInt(clean.slice(4, 6), 16),
  };
}

// Relative luminance, the WCAG definition.
function luminance({ r, g, b }) {
  const channel = (v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

// Brighten a colour until it clears a luminance floor against the dark card.
// Raiders black and Patriots navy are brand-correct and nearly invisible on
// slate-900; this keeps all 32 legible without hand-tuning 32 values and
// getting several of them subtly wrong.
//
// Channels are SCALED, not blended toward white. Blending drags a navy through
// grey — Patriots #002244 came out #677B8E, which is not a colour anyone
// associates with New England. Scaling holds the ratio between channels, so
// hue and saturation both survive and the navy stays navy.
const MIN_LUMINANCE = 0.09;

function lift(hex) {
  const rgb = hexToRgb(hex);
  if (luminance(rgb) >= MIN_LUMINANCE) return hex;

  const { r, g, b } = rgb;
  // Pure black has no ratio to preserve; scaling it stays black forever.
  if (r + g + b === 0) return "#8A8F98";

  const toHex = (v) => Math.round(v).toString(16).padStart(2, "0");
  let scaled = { r, g, b };

  for (let k = 1.1; k <= 12; k += 0.1) {
    scaled = {
      r: Math.min(255, r * k),
      g: Math.min(255, g * k),
      b: Math.min(255, b * k),
    };
    if (luminance(scaled) >= MIN_LUMINANCE) break;
  }
  return `#${toHex(scaled.r)}${toHex(scaled.g)}${toHex(scaled.b)}`;
}

// How colourful a hex is, 0 (grey) to 1 (fully saturated).
function chroma(hex) {
  const { r, g, b } = hexToRgb(hex);
  return (Math.max(r, g, b) - Math.min(r, g, b)) / 255;
}

// Lifting preserves hue, which is right for a navy or a purple but useless for
// a near-black: Chicago's #0B162A has almost no hue left to preserve and comes
// out a flat grey. Those teams are recognised by their secondary — Bears
// orange, Texans red, Raiders silver — so pick that instead when the primary
// is too dark to survive and the secondary actually carries colour.
const TOO_DARK_TO_LIFT = 0.015;

function accentBase(team) {
  const { primary, secondary } = colorsFor(team);
  if (luminance(hexToRgb(primary)) >= TOO_DARK_TO_LIFT) return primary;
  return chroma(secondary) > chroma(primary) ? secondary : primary;
}

const accentCache = new Map();

/** Brand colour, guaranteed visible against the dark card. */
export function teamAccent(team) {
  const key = team || "__fallback";
  if (!accentCache.has(key)) accentCache.set(key, lift(accentBase(team)));
  return accentCache.get(key);
}

/** The same colour at low opacity, for logo-chip fills and rings. */
export function teamTint(team, alpha = 0.14) {
  const { r, g, b } = hexToRgb(teamAccent(team));
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export function teamSecondary(team) {
  return lift(colorsFor(team).secondary);
}
